import {Injectable} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {StorageService} from '../services/storage.service';
import {NativeSecureSession, NativeSecureSessionKey, NativeSecureSessionValues} from './native-secure-session.plugin';

/**
 * Keeps the native vault in sync while Angular is still the authenticated
 * WebView runtime. Flutter reads only the native vault.
 *
 * The WebView copy deliberately remains during this compatibility phase:
 * Angular's synchronous JWT interceptor still depends on it. It will be
 * removed only together with its in-memory replacement, never mid-session.
 */
@Injectable({providedIn: 'root'})
export class NativeSecureSessionService {
  private initialized = false;

  constructor(private readonly storage: StorageService) {
  }

  public async initialize(): Promise<void> {
    if (this.initialized || !Capacitor.isNativePlatform()) {
      return;
    }
    this.initialized = true;

    try {
      const secure = await NativeSecureSession.read();
      if (secure.token) {
        this.restoreLegacyRuntime(secure);
        return;
      }
      await this.write(this.readLegacyRuntime());
    } catch {
      // A missing plugin must never prevent Angular's existing login flow.
      // The Flutter shell flag stays disabled until both native hosts ship it.
    }
  }

  public async write(values: NativeSecureSessionValues): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      await NativeSecureSession.write(values);
    } catch {
      // See initialize(): this is an additive migration path.
    }
  }

  public async clear(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      await NativeSecureSession.clear();
    } catch {
      // Native cleanup cannot block the established Angular logout path.
    }
  }

  public async remove(keys: NativeSecureSessionKey[]): Promise<void> {
    if (!Capacitor.isNativePlatform() || !keys.length) {
      return;
    }
    try {
      await NativeSecureSession.remove({keys});
    } catch {
      // See initialize(): this is an additive migration path.
    }
  }

  private readLegacyRuntime(): NativeSecureSessionValues {
    return {
      token: this.storage.getItem('token') || undefined,
      refreshToken: this.storage.getItem('refresh_token') || undefined,
      tokenExpiration: this.storage.getItem('token_expiration') || undefined,
      remember: this.storage.getItem('remember') || undefined,
    };
  }

  private restoreLegacyRuntime(values: NativeSecureSessionValues): void {
    if (values.token) {
      this.storage.setItem('token', values.token);
    }
    if (values.refreshToken) {
      this.storage.setItem('refresh_token', values.refreshToken);
    }
    if (values.tokenExpiration) {
      this.storage.setItem('token_expiration', values.tokenExpiration);
    }
    if (values.remember) {
      this.storage.setItem('remember', values.remember);
    }
  }
}
